/**
 * Expense Reports List View
 *
 * Displays all expense reports for the current user with:
 * - Table/list of reports with status badges
 * - Status filtering dropdown
 * - Actions (View, Delete for drafts)
 * - Empty state when no reports
 */

import { renderIcon } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import { createExpenseStatusBadge } from '../components/expense-status-badge';
import { showToast } from '../components/toast';
import { expenseReportsStore, type ExpenseReportsState } from '../services/expense-reports-store';
import type { ExpenseReportSummary, ExpenseReportStatus } from '../types/expense-tracker';
import { formatCurrency } from '../types/expense-tracker';

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Navigate to a path
 */
function navigateTo(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Create status filter dropdown
 */
function createStatusFilter(
  currentFilter: ExpenseReportStatus | null,
  onChange: (status: ExpenseReportStatus | null) => void
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'relative';

  const select = document.createElement('select');
  select.className = `
    px-3 py-2 bg-background border border-input rounded-lg
    text-sm text-foreground
    focus:outline-none focus:ring-2 focus:ring-ring
    cursor-pointer appearance-none pr-8
  `.replace(/\s+/g, ' ').trim();

  const options: Array<{ value: string; label: string }> = [
    { value: '', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PAID', label: 'Paid' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  options.forEach((opt) => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    option.selected = opt.value === (currentFilter ?? '');
    select.appendChild(option);
  });

  select.addEventListener('change', () => {
    const value = select.value as ExpenseReportStatus | '';
    onChange(value || null);
  });

  // Dropdown icon
  const iconWrapper = document.createElement('div');
  iconWrapper.className = 'absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground';
  iconWrapper.appendChild(renderIcon('chevronDown', { size: 'sm' }));

  container.appendChild(select);
  container.appendChild(iconWrapper);

  return container;
}

/**
 * Create report row for the table
 */
function createReportRow(
  report: ExpenseReportSummary,
  onView: () => void,
  onDelete: () => void
): HTMLElement {
  const row = document.createElement('tr');
  row.className = 'border-b border-border hover:bg-muted/50 transition-colors';
  row.setAttribute('data-testid', 'expense-report-row');

  row.innerHTML = `
    <td class="px-4 py-3">
      <div>
        <p class="font-medium text-foreground">${report.request_title}</p>
        <p class="text-xs text-muted-foreground">Created ${formatDate(report.created_at)}</p>
      </div>
    </td>
    <td class="px-4 py-3">
      <div id="status-badge-${report.id}"></div>
    </td>
    <td class="px-4 py-3 text-right">
      <span class="font-medium">${formatCurrency(report.total_amount_chf, 'CHF')}</span>
    </td>
    <td class="px-4 py-3 text-center">
      <span class="inline-flex items-center gap-1">
        ${renderIcon('receipt', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
        <span>${report.receipt_count}</span>
      </span>
    </td>
    <td class="px-4 py-3 text-right">
      ${report.submitted_at ? formatDate(report.submitted_at) : '—'}
    </td>
    <td class="px-4 py-3">
      <div class="flex items-center justify-end gap-2" id="actions-${report.id}"></div>
    </td>
  `;

  // Add status badge
  const badgeContainer = row.querySelector(`#status-badge-${report.id}`);
  if (badgeContainer) {
    badgeContainer.appendChild(createExpenseStatusBadge(report.status, { size: 'sm' }));
  }

  // Add action buttons
  const actionsContainer = row.querySelector(`#actions-${report.id}`);
  if (actionsContainer) {
    // View button
    const viewBtn = document.createElement('button');
    viewBtn.className = 'p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground';
    viewBtn.title = 'View';
    viewBtn.appendChild(renderIcon('eye', { size: 'sm' }));
    viewBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onView();
    });
    actionsContainer.appendChild(viewBtn);

    // Delete button (only for drafts)
    if (report.status === 'DRAFT') {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'p-2 rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive';
      deleteBtn.title = 'Delete';
      deleteBtn.appendChild(renderIcon('trash', { size: 'sm' }));
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onDelete();
      });
      actionsContainer.appendChild(deleteBtn);
    }
  }

  // Make entire row clickable
  row.style.cursor = 'pointer';
  row.addEventListener('click', onView);

  return row;
}

/**
 * Create empty state
 */
function createEmptyState(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';

  container.innerHTML = `
    <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
      ${renderIcon('fileText', { size: 'xl', className: 'text-muted-foreground' }).outerHTML}
    </div>
    <h3 class="text-xl font-semibold text-foreground mb-2">No expense reports yet</h3>
    <p class="text-muted-foreground mb-6 max-w-md mx-auto">
      Create an expense report from any expenditure request to start uploading receipts.
      You can add receipts at any time, but submission requires an approved request.
    </p>
    <a href="/my-requests" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
      ${renderIcon('search', { size: 'sm' }).outerHTML}
      <span>View My Requests</span>
    </a>
  `;

  // Handle link click
  const link = container.querySelector('a');
  if (link) {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      navigateTo('/my-requests');
    });
  }

  return container;
}

/**
 * Create loading state
 */
function createLoadingState(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';
  container.innerHTML = `
    <div class="animate-spin w-8 h-8 mx-auto mb-4 text-primary">
      ${renderIcon('loader', { size: 'lg' }).outerHTML}
    </div>
    <p class="text-muted-foreground">Loading expense reports...</p>
  `;
  return container;
}

/**
 * Create error state
 */
function createErrorState(error: string, onRetry: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';

  container.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
      ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
    </div>
    <h3 class="text-lg font-semibold text-foreground mb-2">Failed to load expense reports</h3>
    <p class="text-muted-foreground mb-4">${error}</p>
  `;

  const retryBtn = document.createElement('button');
  retryBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
  retryBtn.innerHTML = `
    ${renderIcon('refresh', { size: 'sm' }).outerHTML}
    <span>Try Again</span>
  `;
  retryBtn.addEventListener('click', onRetry);
  container.appendChild(retryBtn);

  return container;
}

/**
 * Create delete confirmation modal
 */
function showDeleteConfirmation(report: ExpenseReportSummary): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4';

    const modal = document.createElement('div');
    modal.className = 'glass-card rounded-2xl p-6 max-w-md w-full';

    modal.innerHTML = `
      <div class="text-center">
        <div class="w-12 h-12 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
          ${renderIcon('trash', { size: 'lg', className: 'text-destructive' }).outerHTML}
        </div>
        <h3 class="text-lg font-semibold text-foreground mb-2">Delete Expense Report?</h3>
        <p class="text-muted-foreground mb-6">
          This will permanently delete the expense report for "${report.request_title}" and all attached receipts.
          This action cannot be undone.
        </p>
        <div class="flex gap-3 justify-center">
          <button id="cancel-btn" class="px-4 py-2 bg-muted text-foreground font-medium rounded-lg hover:bg-muted/80 transition-colors">
            Cancel
          </button>
          <button id="confirm-btn" class="px-4 py-2 bg-destructive text-destructive-foreground font-medium rounded-lg hover:bg-destructive/90 transition-colors">
            Delete
          </button>
        </div>
      </div>
    `;

    const cancelBtn = modal.querySelector('#cancel-btn');
    const confirmBtn = modal.querySelector('#confirm-btn');

    const cleanup = () => {
      overlay.remove();
    };

    cancelBtn?.addEventListener('click', () => {
      cleanup();
      resolve(false);
    });

    confirmBtn?.addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup();
        resolve(false);
      }
    });

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  });
}

/**
 * Render the reports table
 */
function renderReportsTable(
  container: HTMLElement,
  reports: ExpenseReportSummary[]
): void {
  container.innerHTML = '';

  const table = document.createElement('table');
  table.className = 'w-full';
  table.setAttribute('data-testid', 'expense-reports-list');

  // Table header
  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr class="border-b border-border text-left text-sm text-muted-foreground">
      <th class="px-4 py-3 font-medium">Request</th>
      <th class="px-4 py-3 font-medium">Status</th>
      <th class="px-4 py-3 font-medium text-right">Total</th>
      <th class="px-4 py-3 font-medium text-center">Receipts</th>
      <th class="px-4 py-3 font-medium text-right">Submitted</th>
      <th class="px-4 py-3 font-medium text-right">Actions</th>
    </tr>
  `;
  table.appendChild(thead);

  // Table body
  const tbody = document.createElement('tbody');
  reports.forEach((report) => {
    const row = createReportRow(
      report,
      () => navigateTo(`/expense-reports/${report.id}`),
      async () => {
        const confirmed = await showDeleteConfirmation(report);
        if (confirmed) {
          try {
            await expenseReportsStore.deleteReport(report.id);
            showToast({ type: 'success', message: 'Expense report deleted' });
          } catch (error) {
            showToast({
              type: 'error',
              message: error instanceof Error ? error.message : 'Failed to delete report',
            });
          }
        }
      }
    );
    tbody.appendChild(row);
  });
  table.appendChild(tbody);

  container.appendChild(table);
}

/**
 * Create the expense reports list view
 */
export function expenseReportsView(): HTMLElement {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Page header
  const header = document.createElement('div');
  header.className = 'flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6';

  const headerLeft = document.createElement('div');
  headerLeft.innerHTML = `
    <p class="text-muted-foreground">Track and manage your expense reimbursements</p>
  `;

  const headerRight = document.createElement('div');
  headerRight.className = 'flex items-center gap-3';
  headerRight.id = 'filter-container';

  header.appendChild(headerLeft);
  header.appendChild(headerRight);
  content.appendChild(header);

  // Content container
  const contentContainer = document.createElement('div');
  contentContainer.className = 'glass-card rounded-2xl overflow-hidden';
  contentContainer.id = 'reports-content';
  content.appendChild(contentContainer);

  // Store subscription
  let unsubscribe: (() => void) | null = null;

  /**
   * Render content based on state
   */
  function render(state: ExpenseReportsState): void {
    const container = document.getElementById('reports-content');
    const filterContainer = document.getElementById('filter-container');

    if (!container) return;

    // Render filter dropdown
    if (filterContainer) {
      if (filterContainer.childElementCount === 0) {
        const filter = createStatusFilter(state.statusFilter, async (status) => {
          await expenseReportsStore.setStatusFilter(status);
        });
        filterContainer.appendChild(filter);
      } else {
        // Update existing filter dropdown value to match state
        const select = filterContainer.querySelector('select');
        if (select && select.value !== (state.statusFilter ?? '')) {
          select.value = state.statusFilter ?? '';
        }
      }
    }

    // Loading state
    if (state.loading) {
      container.innerHTML = '';
      container.appendChild(createLoadingState());
      return;
    }

    // Error state
    if (state.error) {
      container.innerHTML = '';
      container.appendChild(createErrorState(state.error, () => {
        expenseReportsStore.fetchReports(state.statusFilter ?? undefined);
      }));
      return;
    }

    // Empty state
    if (state.reports.length === 0) {
      container.innerHTML = '';
      container.appendChild(createEmptyState());
      return;
    }

    // Render reports table
    renderReportsTable(container, state.reports);
  }

  // Subscribe to store and initial fetch
  setTimeout(() => {
    unsubscribe = expenseReportsStore.subscribe(render);

    // Parse status filter from URL query params (e.g., ?status=DRAFT)
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get('status');
    const validStatuses: ExpenseReportStatus[] = ['DRAFT', 'SUBMITTED', 'PAID', 'CANCELLED'];

    if (statusParam && validStatuses.includes(statusParam as ExpenseReportStatus)) {
      expenseReportsStore.setStatusFilter(statusParam as ExpenseReportStatus);
    } else {
      expenseReportsStore.fetchReports();
    }
  }, 0);

  // Cleanup on unmount (view change)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === content && unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
      });
    });
  });

  // Observe parent for removal
  setTimeout(() => {
    if (content.parentElement) {
      observer.observe(content.parentElement, { childList: true });
    }
  }, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/expense-reports',
    title: 'Expense Reports',
    subtitle: 'Track and manage your expense reimbursements',
  });
}

export default expenseReportsView;
